import styles from "./Leaderboard.module.css";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { MdStars } from "react-icons/md";
import { TbUserSquare } from "react-icons/tb";
import { Pagination } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://admin.osleague.online/api";

const getImage = (img) => {
  if (!img || img.trim() === "" || img === "null") return "/Margin.png";
  return img;
};

export default function Leaderboard() {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const currentUser = useSelector((state) => {
    const u = state.auth.user;
    return u?.user || u;
  });

  const [leaderboardData, setLeaderboardData] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [myRankIndex, setMyRankIndex] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [topThree, setTopThree] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [leaderboardRes, myRankRes] = await Promise.all([
          axios.get(`${BASE_URL}/leaderboard/local`, {
            headers,
            params: { page: paginationModel.page + 1 },
          }),
          axios.get(`${BASE_URL}/local-leaderboard/my-rank`, { headers }),
        ]);

        const lData = leaderboardRes.data.data;
        const myRankData = myRankRes.data.data.my_rank;

        setMeta(lData.meta);
        setLeaderboardData(lData.users);
        setTotalPages(lData.pagination.last_page);
        setMyRank(myRankData);

        if (paginationModel.page === 0) {
          setTopThree(lData.users.slice(0, 3));
        }

        const myUserId = myRankData?.user_id;
        const myIndex = lData.users.findIndex((u) => u.user.id === myUserId);
        setMyRankIndex(myIndex !== -1 ? myIndex + 1 : null);
      } catch (error) {
        console.error("Error fetching leaderboard:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, paginationModel.page]);

  const tableRows = leaderboardData.map((item, index) => {
    const isMe = item.user.id === currentUser?.id;

    let image = "/Margin.png";
    if (isMe && currentUser?.image) {
      image = getImage(currentUser.image);
    } else if (item.user?.image) {
      image = getImage(item.user.image);
    } else if (item.user?.profile_image) {
      image = getImage(item.user.profile_image);
    } else if (item.user?.avatar) {
      image = getImage(item.user.avatar);
    }

    return {
      id: item.user.id,
      name: item.user.name,
      image: isMe ? getImage(currentUser?.image) : getImage(item.user.image),
      accuracy: `${item.accuracy}%`,
      correct: item.correct_predictions_count,
      exact: item.exact_predictions_count,
      double: item.double_applied >= 0.5 ? 1 : 0,
      points: item.points,
      globalRank: paginationModel.page * paginationModel.pageSize + index + 1,
    };
  });

  const columns = [
    {
      field: "rank",
      headerName: t("leaderboard.rank"),
      flex: 0.7,
      minWidth: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "var(--main-color)",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          #{params.row.globalRank}
        </div>
      ),
    },

    {
      field: "name",
      headerName: t("leaderboard.name"),
      flex: 2,
      minWidth: 240,
      sortable: false,

      renderCell: (params) => (
        <div
          className={styles.playerCell}
          onClick={() => navigate(`/dashboard/user/${params.row.id}`)}
          style={{ cursor: "pointer" }}
        >
          <img
            src={params.row.image}
            alt={params.row.name}
            className={styles.tableUserImage}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/Margin.png";
            }}
          />

          <span>{params.row.name}</span>
        </div>
      ),
    },

    {
      field: "correct",
      headerName: "ت/ص",
      flex: 0.7,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "exact",
      headerName: "ST",
      flex: 0.7,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "double",
      headerName: t("leaderboard.double"),
      flex: 0.9,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "points",
      headerName: t("leaderboard.points"),
      flex: 1,
      minWidth: 110,
      sortable: false,
      align: "center",
      headerAlign: "center",

      renderCell: (params) => (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "var(--blue-color)",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {params.value}
        </div>
      ),
    },
  ];

  return (
    <div className={`${styles.leaderBoard} py-3`}>
      <div className={styles.container}>
        <div className={`${styles.header} row`}>
          <div className="col-12 col-lg-6">
            <h1>{t("leaderboard.title")}</h1>
            <p>
              {meta?.league} — {t("leaderboard.subtitle")}
            </p>
          </div>

          <div
            className={`${styles.stats} col-12 col-lg-6 d-flex justify-content-end`}
          >
            <div className={styles.statCard}>
              <div>
                <span>{t("leaderboard.yourRank")}</span>
                <b className={styles.bUser}>
                  {myRankIndex
                    ? `#${myRankIndex}`
                    : myRank?.rank
                      ? `#${myRank.rank}`
                      : "—"}
                </b>
              </div>
              <div className={styles.userIcon}>
                <TbUserSquare size={30} />
              </div>
            </div>

            <div className={styles.statCard}>
              <div>
                <span>{t("leaderboard.nextReward")}</span>
                <b className={styles.bStar}>{myRank?.total_points ?? 0} PTS</b>
              </div>
              <div className={styles.starIcon}>
                <MdStars size={30} />
              </div>
            </div>
          </div>
        </div>

        {topThree.length > 0 && (
          <div className={styles.topGrid}>
            {topThree.map((p, index) => {
              const isMe = p.user.id === currentUser?.id;
              return (
                <div key={p.user.id} className={styles.card}>
                  <div className={styles.playerInfo}>
                    <div className={styles.avatarWrapper}>
                      <img
                        src={
                          isMe
                            ? getImage(currentUser?.image)
                            : getImage(p.user.image)
                        }
                        alt={p.user.name}
                        className={styles.userImage}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/Margin.png";
                        }}
                      />
                      <span className={styles.rankBadge}>{index + 1}</span>
                    </div>
                    <h3>{p.user.name}</h3>
                  </div>
                  <h4 className={styles.points}>{p.points}</h4>
                  <p className={`text-uppercase ${styles.label}`}>
                    {t("leaderboard.ptsTotal")}
                  </p>
                </div>
              );
            })}
          </div>
        )}
        <div className={styles.tableScrollOuter}>
          <div className={styles.tableScrollInner}>
            <Paper className={styles.paper}>
              {loading ? (
                <div className={styles.loader}>{t("common.loading")}</div>
              ) : (
                <DataGrid
                  rows={tableRows}
                  columns={columns}
                  rowHeight={80}
                  disableRowSelectionOnClick
                  hideFooter
                  autoHeight
                  className={styles.dataGrid}
                  columnHeaderHeight={80}
                />
              )}
            </Paper>
          </div>
        </div>
        <div className={styles.paginationWrap}>
          <Pagination
            count={totalPages}
            page={paginationModel.page + 1}
            onChange={(e, value) =>
              setPaginationModel({
                page: value - 1,
                pageSize: 15,
              })
            }
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </div>
      </div>
    </div>
  );
}
